import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DrawOutlined as DrawOutlinedIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import axios from "axios";
import {
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_get_user_signature,
} from "../../../constants";
import { getDefaultAxiosConfig, getRightworkAPI } from "../requests/utilities/requests";
import { useNotifyMobileModal } from "../take_training_employee/useNotifyMobileModal";
import AdoptSignatureModal, { fontFamilyForKey, renderTypedSignatureToPng } from "./AdoptSignatureModal";
import {
  SIGNATURE_DISCLOSURE_VERSION,
  SIGNATURE_DISCLOSURE_HEADING,
  SIGNATURE_DISCLOSURE_POINTS,
  SIGNATURE_DISCLOSURE_FOOTER,
  SIGNATURE_CONSENT_LABEL,
} from "./signatureDisclosure";

// Shared violet focus-border treatment for LMS form inputs (matches
// SectionInputsModal2 / AcknowledgementConfigModal).
const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

// Employee-facing signing flow for an acknowledgement-mode signature block:
// read statement -> agree to e-signature disclosure -> apply a reusable
// signature -> submit. Generates a human-readable PDF artifact and reports the
// audit fields back to the caller via onComplete.
function AcknowledgementSignModal(props) {
  const {
    open,
    handleClose,
    title,
    statement,
    homeCompany,
    onComplete,
    isPreview,
  } = props;

  useNotifyMobileModal(open);

  const [consentChecked, setConsentChecked] = useState(false);
  const [userSignature, setUserSignature] = useState(null);
  const [printedName, setPrintedName] = useState("");
  const [openAdopt, setOpenAdopt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  // Load the user's existing reusable signature when the modal opens.
  useEffect(() => {
    const loadSignature = async () => {
      if (!open) return;
      setConsentChecked(false);
      setIsSubmitting(false);
      setProgress(0);
      setIsCompleted(false);
      try {
        const res = await getRightworkAPI(API_get_user_signature, { company_id: homeCompany });
        const sig = res?.data?.redux?.payload?.user_signature;
        setUserSignature(sig || null);
        if (sig?.method === "typed" && sig?.typed_name) {
          setPrintedName(sig.typed_name);
        }
      } catch (e) {
        // Non-fatal: user can still adopt a signature inline.
        setUserSignature(null);
      }
    };
    loadSignature();
  }, [open, homeCompany]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "96%", md: "70%" },
    maxWidth: { xs: "100%", md: 760 },
    maxHeight: "94vh",
    bgcolor: "background.paper",
    border: { xs: "none", md: "2px solid #E2E8F0" },
    borderRadius: "15px",
    boxShadow: 24,
    overflowY: "auto",
  };

  const renderSignaturePreview = () => {
    if (!userSignature) return null;
    if (userSignature.method === "drawn" && userSignature.image_url) {
      return (
        <img
          src={userSignature.image_url}
          alt="Your signature"
          style={{ maxHeight: 56, maxWidth: "100%" }}
        />
      );
    }
    return (
      <span
        style={{ fontFamily: fontFamilyForKey(userSignature.typed_font), fontSize: 32 }}
        className="text-slate-900"
      >
        {userSignature.typed_name}
      </span>
    );
  };

  // Build a simple, human-readable acknowledgement PDF artifact. Paginates so a
  // long statement (or signature block) never runs off the bottom of the page.
  const generatePdf = async () => {
    const pdfDoc = await PDFDocument.create();
    const PAGE_W = 612;
    const PAGE_H = 792; // US Letter
    const margin = 56;
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    let page = pdfDoc.addPage([PAGE_W, PAGE_H]);
    let y = PAGE_H - margin;

    // Start a fresh page when the next `needed` vertical units won't fit.
    const ensureSpace = (needed) => {
      if (y - needed < margin) {
        page = pdfDoc.addPage([PAGE_W, PAGE_H]);
        y = PAGE_H - margin;
      }
    };

    const drawWrapped = (text, size, f, lineGap) => {
      const maxWidth = PAGE_W - margin * 2;
      const words = (text || "").split(/\s+/);
      let line = "";
      const flush = () => {
        ensureSpace(size + lineGap);
        page.drawText(line, { x: margin, y, size, font: f, color: rgb(0.1, 0.1, 0.1) });
        y -= size + lineGap;
      };
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (f.widthOfTextAtSize(test, size) > maxWidth && line) {
          flush();
          line = word;
        } else {
          line = test;
        }
      }
      if (line) flush();
    };

    if (title) {
      drawWrapped(title, 18, fontBold, 8);
      y -= 12;
    }
    drawWrapped(statement, 12, font, 6);
    y -= 30;

    // Keep the whole signature block (line + signature + name + date) together.
    ensureSpace(110);

    // Signature line
    page.drawLine({
      start: { x: margin, y },
      end: { x: margin + 240, y },
      thickness: 1,
      color: rgb(0.6, 0.6, 0.6),
    });

    if (userSignature?.method === "drawn" && userSignature.image_url) {
      try {
        const imgBytes = await fetch(userSignature.image_url).then((r) => r.arrayBuffer());
        const png = await pdfDoc.embedPng(imgBytes);
        const scaled = png.scaleToFit(200, 48);
        page.drawImage(png, { x: margin, y: y + 4, width: scaled.width, height: scaled.height });
      } catch (e) {
        // fall through to text
      }
    } else if (userSignature?.typed_name) {
      // Embed the typed signature rendered in its chosen font (script/serif/
      // mono), so the PDF matches the adopted style instead of plain Helvetica.
      const dataUrl = renderTypedSignatureToPng(
        userSignature.typed_name,
        userSignature.typed_font
      );
      let embedded = false;
      if (dataUrl) {
        try {
          const imgBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());
          const png = await pdfDoc.embedPng(imgBytes);
          const scaled = png.scaleToFit(240, 56);
          page.drawImage(png, { x: margin, y: y + 4, width: scaled.width, height: scaled.height });
          embedded = true;
        } catch (e) {
          // fall through to text
        }
      }
      if (!embedded) {
        page.drawText(userSignature.typed_name, {
          x: margin,
          y: y + 8,
          size: 22,
          font: fontBold,
          color: rgb(0.1, 0.1, 0.1),
        });
      }
    }

    y -= 18;
    page.drawText(`Printed name: ${printedName}`, { x: margin, y, size: 10, font, color: rgb(0.3, 0.3, 0.3) });
    y -= 16;
    const signedOn = new Date().toLocaleString();
    page.drawText(`Date: ${signedOn}`, { x: margin, y, size: 10, font, color: rgb(0.3, 0.3, 0.3) });

    // Footer / certificate line on the last page
    page.drawText(
      "Signed electronically via Rightwork. This document is accompanied by an audit record.",
      { x: margin, y: margin, size: 8, font, color: rgb(0.5, 0.5, 0.5) }
    );

    return pdfDoc.save();
  };

  const uploadPdf = async (bytes) => {
    const blob = new Blob([bytes], { type: "application/pdf" });
    const fileName = `acknowledgement_${(title || "document").replace(/\s+/g, "_")}.pdf`;
    const file = new File([blob], fileName, { type: "application/pdf" });

    const formData = new FormData();
    formData.append("company_id", homeCompany);
    formData.append("file_name", fileName);
    formData.append("type", "lms_signature_pdf_response");

    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      { headers: { ...axiosConfig.headers, "Content-Type": "multipart/form-data" } }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    setProgress(60);
    await axios.put(presignedUrl, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: (e) => setProgress(60 + Math.round((e.loaded * 30) / (e.total || 1))),
    });
    return fileUUID;
  };

  const handleSubmit = async () => {
    if (isPreview) {
      handleClose();
      return;
    }
    if (!consentChecked) {
      toast.error("Please agree to use electronic records and signatures");
      return;
    }
    if (!userSignature) {
      toast.error("Please add your signature");
      return;
    }
    if (!printedName.trim()) {
      toast.error("Please enter your printed name");
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setProgress(15);
      const bytes = await generatePdf();
      setProgress(45);
      const fileUuid = await uploadPdf(bytes);
      setProgress(95);

      if (onComplete) {
        await onComplete({
          uuid: fileUuid,
          signer_printed_name: printedName.trim(),
          signature_method: userSignature.method,
          user_signature_uuid: userSignature.uuid,
          consent_agreed: true,
          consent_disclosure_version: SIGNATURE_DISCLOSURE_VERSION,
          // Echo the applied signature so the card can render it immediately,
          // before the next hydrated section load supplies it from the backend.
          applied_signature: {
            method: userSignature.method,
            typed_name: userSignature.typed_name,
            typed_font: userSignature.typed_font,
            image_uuid: userSignature.image_uuid,
          },
          applied_signature_image_url: userSignature.image_url || null,
        });
      }

      setProgress(100);
      setIsCompleted(true);
      setTimeout(() => handleClose(), 1400);
    } catch (error) {
      console.error("Error submitting acknowledgement:", error);
      toast.error("Failed to submit. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleAdopted = (sig) => {
    setUserSignature(sig || null);
    if (sig?.method === "typed" && sig?.typed_name && !printedName) {
      setPrintedName(sig.typed_name);
    }
  };

  return (
    <Modal open={open} onClose={isSubmitting ? undefined : handleClose} aria-labelledby="acknowledgement-sign-modal">
      <Box sx={style}>
        {isSubmitting || isCompleted ? (
          <div className="p-8 flex flex-col items-center justify-center min-h-[320px]">
            {isCompleted ? (
              <>
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 48 }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                  Acknowledgement recorded
                </h2>
                <p className="text-slate-600 text-center max-w-md">
                  Your signature has been recorded.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mb-4">
                  <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 48 }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                  Submitting your signature...
                </h2>
                <div className="w-full max-w-md mt-4">
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e5e7eb",
                      "& .MuiLinearProgress-bar": { bgcolor: "#7c3aed" },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-slate-900 truncate">
                      {title || "Acknowledgement"}
                    </h3>
                    {isPreview && (
                      <p className="text-xs text-amber-600 font-medium">
                        Preview Mode - nothing will be saved
                      </p>
                    )}
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shrink-0"
                  onClick={handleClose}
                >
                  <CloseIcon style={{ fontSize: "20px" }} />
                </button>
              </div>
            </div>

            <div className="px-6 py-6 space-y-5">
              {/* Statement */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="text-slate-800 whitespace-pre-wrap" style={{ fontSize: 15, lineHeight: 1.6 }}>
                  {statement}
                </p>
              </div>

              {/* Disclosure */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">
                  {SIGNATURE_DISCLOSURE_HEADING}
                </h4>
                <p className="text-xs text-slate-700 mb-1">By checking the box below, you agree to:</p>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1 ml-1">
                  {SIGNATURE_DISCLOSURE_POINTS.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
                <p className="text-xs text-slate-700 mt-2">{SIGNATURE_DISCLOSURE_FOOTER}</p>
                <label className="flex items-start gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded"
                    style={{ accentColor: "#7c3aed" }}
                  />
                  <span className="text-sm text-slate-700">{SIGNATURE_CONSENT_LABEL}</span>
                </label>
              </div>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your signature</label>
                <div className="border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 min-h-[88px]">
                  <div className="flex-1 flex items-center min-h-[56px]">
                    {userSignature ? (
                      renderSignaturePreview()
                    ) : (
                      <span className="text-slate-400 text-sm">No signature added yet</span>
                    )}
                  </div>
                  <button
                    onClick={() => setOpenAdopt(true)}
                    className="px-4 py-2 border border-violet-300 hover:bg-violet-50 text-violet-700 rounded-lg flex items-center gap-2 shrink-0"
                  >
                    {userSignature ? <EditIcon style={{ fontSize: 16 }} /> : <DrawOutlinedIcon style={{ fontSize: 16 }} />}
                    {userSignature ? "Change" : "Add signature"}
                  </button>
                </div>
              </div>

              {/* Printed name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Printed name</label>
                <TextField
                  placeholder="Type your full name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  InputProps={{ sx: { ...inputSx, height: "45px" } }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!isPreview && (!consentChecked || !userSignature || !printedName.trim())}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                    isPreview
                      ? "bg-violet-600 hover:bg-violet-700 text-white"
                      : consentChecked && userSignature && printedName.trim()
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-slate-300 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  {isPreview ? "Exit Preview" : "Sign & Submit"}
                </button>
              </div>
            </div>
          </>
        )}

        <AdoptSignatureModal
          open={openAdopt}
          handleClose={() => setOpenAdopt(false)}
          homeCompany={homeCompany}
          onAdopted={handleAdopted}
        />
      </Box>
    </Modal>
  );
}

export default AcknowledgementSignModal;
